import { Link } from "expo-router";
import React, { useEffect } from "react";
import { BackHandler, Platform, StyleSheet } from "react-native";
import Animated, {
  FadeIn,
  FadeInDown,
  FadeOut,
  FadeOutDown,
} from "react-native-reanimated";
import { Button } from "~/components/Button";
import { Modal } from "~/components/Modal";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { useEvent } from "~/hooks/useEvent";
import { styleConstants } from "~/style/styleConstants";

export function RulesModal({
  onAccept,
  onCancel: onCancelRaw,
}: {
  onAccept: () => void;
  onCancel: () => void;
}) {
  const onCancel = useEvent(onCancelRaw);
  useEffect(() => {
    if (Platform.OS !== "android") return;

    // Back button should close the modal
    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      () => {
        onCancel();
        return true;
      },
    );

    return () => {
      backHandler.remove();
    };
  }, [onCancel]);

  return (
    <Modal>
      <Animated.View
        entering={FadeIn}
        exiting={FadeOut}
        style={style.container}
      >
        <Animated.View
          entering={FadeInDown}
          exiting={FadeOutDown}
          style={style.card}
        >
          <Text sizeLarge weightExtraBold alignCenter>
            {intl.formatMessage({
              defaultMessage: "Rules",
              id: "80eH0x",
              description:
                "Title for the alert that asks user to read the rules before starting a live video chat",
            })}
          </Text>

          <Spacer medium />

          <Text alignLeft>
            {intl.formatMessage(
              {
                defaultMessage:
                  "By using this video chat, you agree to our <link>guidelines</link>! Here are some rules to ensure a great experience:{br}{br}<bold>Be Respectful:</bold> Treat others with kindness and courtesy.{br}<bold>Keep Your Face Visible:</bold> Ensure your face is visible on camera.{br}<bold>No Harassment:</bold> Any form of harassment or disrespectful behavior will not be tolerated.{br}<bold>Follow Community Guidelines:</bold> Familiarize yourself with our <link>community guidelines</link> and abide by them.{br}{br}Let's create a friendly and safe space for everyone to enjoy!",
                id: "3WcUzW",
                description:
                  "Message for the alert that asks user to start a live video chat",
              },
              {
                br: "\n",
                bold: (chunks) => <Text weightExtraBold>{chunks}</Text>,
                link: (chunks) => (
                  <Link href="https://bokbok.io/guidelines">
                    <Text colorTheme weightExtraBold>
                      {chunks}
                    </Text>
                  </Link>
                ),
              },
            )}
          </Text>

          <Spacer large />

          <View flexDirectionRow style={{ minWidth: 300 }}>
            <Button
              flex
              title={intl.formatMessage({
                defaultMessage: "Accept",
                id: "D7ViN1",
                description: "Text for accept button in overlay",
              })}
              onPress={onAccept}
            />

            <Spacer medium />

            <Button
              flex
              title={intl.formatMessage({
                defaultMessage: "Cancel",
                id: "7tsY9l",
                description: "Cancel button in alert",
              })}
              onPress={onCancel}
              outline
            />
          </View>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}

const style = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: styleConstants.colorBackgroundBackdrop,
    justifyContent: "center",
    padding: styleConstants.spacingExtraLarge,
  },
  card: {
    maxWidth: 600,
    alignSelf: "center",
    justifyContent: "center",
    alignItems: "center",
    padding: styleConstants.spacingLarge,
    backgroundColor: styleConstants.colorBackgroundWhite,
    borderRadius: styleConstants.borderRadiusSmall,
  },
});
